// forms/BullishBearishDetails.js
import React from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon 
} from '@mui/icons-material';
import { SIGNALS, selectSx, TV } from '../../constants';

// Define signal pairs that come from the same indicator
const SIGNAL_PAIRS = {
  'SMART_BAND_12': 'SMART_BAND_12_SHORT',
  'SMART_BAND_12_SHORT': 'SMART_BAND_12',
  'LUX_LONG': 'LUX_SHORT',
  'LUX_SHORT': 'LUX_LONG',
  'LUX_LONG_12': 'LUX_SHORT_12',
  'LUX_SHORT_12': 'LUX_LONG_12',
  'COBRA_LONG': 'COBRA_SHORT',
  'COBRA_SHORT': 'COBRA_LONG',
  'COBRA_LONG_DEFAULT': 'COBRA_SHORT_DEFAULT',
  'COBRA_SHORT_DEFAULT': 'COBRA_LONG_DEFAULT',
  'ALGO_PRO_LONG': 'ALGO_PRO_SHORT',
  'ALGO_PRO_SHORT': 'ALGO_PRO_LONG',
};

const BullishBearishDetails = ({ sub, idx, onUpdate, isBullish = true }) => {
  const typeLabel = isBullish ? 'Bullish' : 'Bearish';
  const iconEmoji = isBullish ? '📈' : '📉';
  const colorTheme = isBullish ? TV.accent.success : TV.accent.danger;
  
  // Get the indicator signal (first signal)
  const indicatorSignal = sub.condition?.first_signal?.name || '';
  
  // Automatically determine the excluded signal based on the indicator
  const autoExcludedSignal = SIGNAL_PAIRS[indicatorSignal] || null;
  
  // When indicator signal changes, update the condition with auto-excluded signal
  const handleIndicatorChange = (value) => {
    const oppositeSignal = SIGNAL_PAIRS[value];
    const excludedSignals = oppositeSignal ? [oppositeSignal] : [];
    
    onUpdate(idx, 'condition', { 
      ...sub.condition, 
      first_signal: { name: value, timeframe: sub.condition?.first_signal?.timeframe || '1m' },
      excluded_signals: excludedSignals,
      reset_on_excluded: true
    });
  };

  // Helper to get appropriate signals for bullish/bearish
  const getFilteredSignals = () => {
    if (isBullish) {
      // For bullish, prefer signals ending in _LONG or beginning with positive indicators
      return SIGNALS.filter(sig => 
        sig.includes('LONG') || 
        sig === 'SMART_BAND_12' ||
        sig === 'SMART_TRAIL_2' ||
        sig === 'SMART_TRAIL_3'
      );
    } else {
      // For bearish, prefer signals ending in _SHORT or bearish indicators
      return SIGNALS.filter(sig => 
        sig.includes('SHORT') || 
        sig === 'SMART_BAND_12_SHORT'
      );
    }
  };

  const filteredIndicatorSignals = getFilteredSignals();
  const confirmationSignals = SIGNALS; // All signals available for confirmation

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          {isBullish ? (
            <TrendingUpIcon sx={{ fontSize: '20px', color: colorTheme }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: '20px', color: colorTheme }} />
          )}
          <Typography sx={{ 
            fontSize: '11px', 
            color: colorTheme, 
            fontWeight: 600, 
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {iconEmoji} {typeLabel} Signal Detection Pattern
          </Typography>
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Alert 
          severity="info" 
          sx={{ 
            bgcolor: `${colorTheme}10`,
            color: TV.text.primary,
            border: `1px solid ${colorTheme}30`,
            fontSize: '11px',
            '& .MuiAlert-icon': { color: colorTheme }
          }}
        >
          <Typography sx={{ fontSize: '11px', fontWeight: 600, mb: 0.5 }}>
            How {typeLabel} Detection Works:
          </Typography>
          <Typography sx={{ fontSize: '10px', lineHeight: 1.6 }}>
            1. Select the {typeLabel.toLowerCase()} indicator signal (e.g., {isBullish ? 'SMART_BAND_12' : 'SMART_BAND_12_SHORT'})<br/>
            2. Select the confirmation signal (e.g., {isBullish ? 'COBRA_LONG' : 'COBRA_SHORT'})<br/>
            3. The indicator must be the most recent signal when confirmation arrives<br/>
            4. Opposite signal ({autoExcludedSignal || 'auto-detected'}) will reset if it appears between them
          </Typography>
        </Alert>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>{typeLabel} Indicator Signal</InputLabel>
          <Select 
            value={indicatorSignal} 
            label={`${typeLabel} Indicator Signal`}
            onChange={(e) => handleIndicatorChange(e.target.value)}
            sx={{
              ...selectSx,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: indicatorSignal ? colorTheme : TV.border.primary,
              }
            }}
          >
            <MenuItem value="">-- Select Indicator --</MenuItem>
            {filteredIndicatorSignals.map(signal => (
              <MenuItem key={signal} value={signal}>
                {signal}
                {SIGNAL_PAIRS[signal] && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      ml: 1, 
                      fontSize: '10px', 
                      color: TV.text.tertiary 
                    }}
                  >
                    (excludes {SIGNAL_PAIRS[signal]})
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel>Confirmation Signal</InputLabel>
          <Select 
            value={sub.condition?.second_signal?.name || ''} 
            label="Confirmation Signal"
            onChange={(e) => onUpdate(idx, 'condition', { 
              ...sub.condition, 
              second_signal: { name: e.target.value, timeframe: sub.condition?.second_signal?.timeframe || '1m' } 
            })}
            sx={selectSx}
          >
            <MenuItem value="">-- Select Confirmation --</MenuItem>
            {confirmationSignals.map(signal => (
              <MenuItem key={signal} value={signal}>
                {signal}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {indicatorSignal && (
        <Grid item xs={12}>
          <Box sx={{ 
            p: 1.5, 
            bgcolor: TV.bg.secondary, 
            borderRadius: '4px',
            border: `1px solid ${TV.border.primary}`
          }}>
            <Typography sx={{ fontSize: '10px', color: TV.text.secondary, fontWeight: 600, mb: 1 }}>
              PATTERN CONFIGURATION:
            </Typography>
            
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: '11px', color: TV.text.secondary }}>
                  {typeLabel} Indicator:
                </Typography>
                <Chip 
                  label={indicatorSignal}
                  size="small"
                  sx={{ 
                    bgcolor: `${colorTheme}20`,
                    color: colorTheme,
                    fontSize: '10px',
                    height: '20px',
                    fontWeight: 600
                  }}
                />
              </Stack>

              {autoExcludedSignal && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: '11px', color: TV.text.secondary }}>
                    Auto-Excluded (Resets):
                  </Typography>
                  <Chip 
                    label={autoExcludedSignal}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(233, 30, 99, 0.1)',
                      color: TV.accent.danger,
                      fontSize: '10px',
                      height: '20px',
                      fontWeight: 600
                    }}
                  />
                </Stack>
              )}

              {sub.condition?.second_signal?.name && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: '11px', color: TV.text.secondary }}>
                    Confirmation:
                  </Typography>
                  <Chip 
                    label={sub.condition.second_signal.name}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(41, 98, 255, 0.1)',
                      color: TV.accent.primary,
                      fontSize: '10px',
                      height: '20px',
                      fontWeight: 600
                    }}
                  />
                </Stack>
              )}
            </Stack>

            <Typography sx={{ fontSize: '10px', color: TV.text.tertiary, mt: 1.5, fontStyle: 'italic' }}>
              ✓ {indicatorSignal} must be active (most recent) when {sub.condition?.second_signal?.name || 'confirmation'} arrives<br/>
              ✗ If {autoExcludedSignal} appears between them, the pattern resets
            </Typography>
          </Box>
        </Grid>
      )}

      <Grid item xs={12}>
        <Box sx={{ 
          p: 1, 
          bgcolor: 'rgba(41, 98, 255, 0.05)', 
          borderRadius: '4px',
          border: `1px solid ${TV.border.primary}`
        }}>
          <Typography sx={{ fontSize: '10px', color: TV.text.secondary, lineHeight: 1.6 }}>
            <strong style={{ color: colorTheme }}>Detection Logic:</strong><br/>
            • {indicatorSignal || '[Indicator]'} arrives → Market is {typeLabel.toLowerCase()}<br/>
            • {sub.condition?.second_signal?.name || '[Confirmation]'} can arrive simultaneously or after<br/>
            • If {autoExcludedSignal || '[Opposite]'} appears, market structure changed → Reset<br/>
            • Both signals detected = Entry condition met ✓
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default BullishBearishDetails;
